import {
  BULK_EVENT_SETTINGS_FIELD_LABELS,
  validateUniversalBulkSettingsPatch,
} from '@blocktickets/shared-utils'

export const BULK_SETTINGS_CHUNK_SIZE = 25

export function buildBulkSettingsPatch(values) {
  const patch = {}
  if (!values) return patch

  if (values.includeIsVisible) patch.isVisible = Boolean(values.isVisible)
  if (values.includeEventVisibility && values.eventVisibility) {
    patch.eventVisibility = values.eventVisibility
  }
  if (values.includeGeneralOnsale && values.generalOnsale) {
    patch.generalOnsale = values.generalOnsale
  }
  if (values.includeFacilityFees && values.facilityFees !== '') {
    patch.facilityFees = Number(values.facilityFees)
  }
  if (values.includeFeeFlags) {
    patch.includeTaxes = Boolean(values.includeTaxes)
    patch.includeFees = Boolean(values.includeFees)
    patch.includeServiceFees = Boolean(values.includeServiceFees)
  }
  if (values.includeTicketLimit) {
    patch.ticketLimit = Boolean(values.ticketLimit)
    patch.globalTicketLimit = values.ticketLimit
      ? Number(values.globalTicketLimit)
      : null
  }
  if (values.includeResale) {
    patch.resaleEnableMinimum = Boolean(values.resaleEnableMinimum)
    if (values.resaleEnableMinimum) {
      patch.resaleMinimumPercentage = Number(values.resaleMinimumPercentage)
    }
  }
  if (values.includeEnableResale) {
    patch.enableResale = Boolean(values.enableResale)
  }
  if (values.includeWaitingRoom) {
    patch.waitingRoomEnabled = Boolean(values.waitingRoomEnabled)
    if (values.waitingRoomEnabled) {
      patch.waitingRoomMaxConcurrent = Number(values.waitingRoomMaxConcurrent)
      patch.waitingRoomAdmissionTtlMinutes = Number(
        values.waitingRoomAdmissionTtlMinutes
      )
    }
  }
  if (values.includeImage && values.image != null && values.image !== '') {
    patch.image = values.image
  }
  if (values.includeDescription) patch.description = values.description || ''
  if (values.includePromotionalDescription) {
    patch.promotionalDescription = values.promotionalDescription || ''
  }
  if (values.includeInvoiceEmailNote) {
    patch.invoiceEmailNote = values.invoiceEmailNote || ''
  }
  if (values.includeCategory) {
    patch.category =
      values.category === '' || values.category == null
        ? null
        : Number(values.category)
  }
  if (values.includeSubcategories) {
    const ids = Array.isArray(values.sub_categories)
      ? values.sub_categories
      : []
    patch.sub_categories = [
      ...new Set(
        ids
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
      ),
    ]
  } else if (values.includeCategory) {
    patch.sub_categories = []
  }

  return patch
}

export function bulkSettingsFieldLabels(patch) {
  return Object.keys(patch || {}).map(
    (field) => BULK_EVENT_SETTINGS_FIELD_LABELS[field] || field
  )
}

export function validateBulkSettingsPatch(patch, options) {
  return validateUniversalBulkSettingsPatch(patch, options)
}

function failureItem(event, message, reasonCode) {
  return {
    eventUUID: event.eventUUID,
    eventName: event.eventName || null,
    status: 'FAILED',
    reason: message,
    reasonCode,
    updatedFields: [],
    timestamp: new Date().toISOString(),
  }
}

function errorMessage(error) {
  const message =
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    'Unexpected error'
  if (message === 'Internal Server Error') {
    return 'The update could not be completed. Try again.'
  }
  return message
}

export function chunkEvents(events, size = BULK_SETTINGS_CHUNK_SIZE) {
  const chunks = []
  for (let index = 0; index < events.length; index += size) {
    chunks.push(events.slice(index, index + size))
  }
  return chunks
}

export function exportBulkSettingsCsv(result) {
  const rows = [
    [
      'Status',
      'Event Name',
      'Event UUID',
      'Reason',
      'Updated Fields',
      'Timestamp',
    ],
  ]
  ;['updated', 'skipped', 'failed'].forEach((key) => {
    ;(result?.[key] || []).forEach((item) => {
      rows.push([
        item.status,
        item.eventName || '',
        item.eventUUID || '',
        item.reason || '',
        (item.updatedFields || []).join('; '),
        item.timestamp || '',
      ])
    })
  })

  const csv = rows
    .map((row) =>
      row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
    )
    .join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'bulk-event-settings-results.csv'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Sends the same patch in independent chunks so one timeout does not
 * discard events that have not been sent yet. Request-level rejection
 * stops the remaining chunks.
 */
export async function runBulkSettingsUpdate({
  events,
  patch,
  organizationUUID,
  request,
  chunkSize = BULK_SETTINGS_CHUNK_SIZE,
  onProgress,
}) {
  const aggregate = { updated: [], skipped: [], failed: [] }
  const groups = chunkEvents(events, chunkSize)
  let completed = 0

  for (let index = 0; index < groups.length; index += 1) {
    const group = groups[index]
    let stopCode = null
    let stopMessage = ''
    try {
      const response = await request({
        organizationUUID,
        eventUuids: group.map((event) => event.eventUUID),
        patch,
      })
      const body = response?.data || {}
      aggregate.updated.push(...(body.updated || []))
      aggregate.skipped.push(...(body.skipped || []))
      aggregate.failed.push(...(body.failed || []))
    } catch (error) {
      const status = error?.response?.status
      stopMessage = errorMessage(error)
      const reasonCode =
        status === 400
          ? 'validation'
          : status === 401 || status === 403
            ? 'authorization'
            : 'unexpected'
      aggregate.failed.push(
        ...group.map((event) => failureItem(event, stopMessage, reasonCode))
      )
      if (reasonCode !== 'unexpected') stopCode = reasonCode
    }

    completed += group.length
    if (onProgress) {
      onProgress({
        completed: Math.min(completed, events.length),
        total: events.length,
      })
    }

    if (stopCode) {
      const remaining = events.slice(completed)
      aggregate.failed.push(
        ...remaining.map((event) =>
          failureItem(
            event,
            'Not processed because the update was rejected.',
            stopCode
          )
        )
      )
      if (onProgress)
        onProgress({ completed: events.length, total: events.length })
      break
    }
  }

  return aggregate
}
